import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { useState, useMemo } from "react";
import {
  Trash2,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  User,
  Building,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  SearchFilterBar,
  type FilterSettings,
} from "@/components/ui/search-filter-bar";
import { filterData } from "@/lib/filter-utils";
import type { ProjectMember } from "@/types/project.types";
import { apiService } from "@/lib/api-service";

interface ProjectMembersTableProps {
  projectId: string;
  projectStatus: string;
}

export const ProjectMembersTable = ({
  projectId,
  projectStatus,
}: ProjectMembersTableProps) => {
  const queryClient = useQueryClient();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filterSettings, setFilterSettings] = useState<FilterSettings>({
    searchTerm: "",
    filters: {},
  });

  const {
    data: members,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["project-members", projectId],
    queryFn: () =>
      apiService.get<ProjectMember[]>(`/projects/${projectId}/members`),
  });

  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiService.delete(`/projects/${projectId}/members/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["project-members", projectId],
      });
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      queryClient.invalidateQueries({
        queryKey: ["project-revision-history", projectId],
      });
    },
  });

  const handleRemove = (id: string) => {
    removeMutation.mutate(id);
  };

  // Define table columns
  const columns = useMemo<ColumnDef<ProjectMember>[]>(
    () => [
      {
        accessorKey: "name",
        header: ({ column }) => (
          <Button
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <User className="h-4 w-4" />
            Name
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </Button>
        ),
        cell: ({ row }) => (
          <div className="font-medium flex items-center gap-3">
            <div className="flex items-center justify-center p-3 bg-muted rounded-lg">
              <User className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex flex-col gap-1">
              <p>{row.getValue("name")}</p>
              <p className="text-xs text-muted-foreground">
                {row.original.email}
              </p>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "department",
        header: ({ column }) => (
          <Button
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
          >
            <Building className="h-4 w-4" />
            Department
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4" />
            )}
          </Button>
        ),
        cell: ({ row }) => (
          <div className="text-sm">{row.getValue("department")}</div>
        ),
      },

      {
        id: "actions",
        header: " ",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-right">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRemove(row.original.id)}
              disabled={removeMutation.isPending || projectStatus !== "ACTIVE"}
              className="text-destructive hover:text-destructive"
              title={
                projectStatus !== "ACTIVE"
                  ? "Cannot remove members from non-active projects"
                  : "Remove member"
              }
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [removeMutation.isPending, projectStatus]
  );

  // Filter data based on search and filter settings
  const filteredData = useMemo(() => {
    if (!members) return [];
    return filterData(members, filterSettings, ["name", "email"]);
  }, [members, filterSettings]);

  // Set up React Table
  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  // Filter options for the search filter bar
  const filterOptions = [
    {
      key: "department",
      label: "Department",
      type: "select" as const,
      options: useMemo(() => {
        if (!members) return [];
        const departments = [...new Set(members.map((m) => m.department))];
        return departments.map((dept) => ({ value: dept, label: dept }));
      }, [members]),
    },
  ];

  if (isLoading) return <div>Loading...</div>;
  if (isError)
    return <div className="text-red-500">Failed to load project members.</div>;

  return (
    <div className="space-y-4">
      {/* Search and Filter Bar */}
      <SearchFilterBar
        filterOptions={filterOptions}
        placeholder="Search members by name or email..."
        onFilterChange={setFilterSettings}
      />

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="!bg-muted/40">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="hover:bg-transparent"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {filterSettings.searchTerm ||
                  Object.keys(filterSettings.filters).length > 0
                    ? "No members found matching your search criteria."
                    : "No members found."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Results Summary */}
      {members && (
        <div className="text-sm text-muted-foreground">
          Showing {filteredData.length} of {members.length} members
          {(filterSettings.searchTerm ||
            Object.keys(filterSettings.filters).length > 0) && (
            <span> (filtered)</span>
          )}
        </div>
      )}
    </div>
  );
};
